import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, MessageCircle, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { openIdRequest } from "@/lib/idRequest";
import { useAuth } from "@/contexts/AuthContext";
import { formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * "Collect KYC" / "Take KYC" — offers staff two ways to get a customer's KYC:
 * fill it in at the counter now, or WhatsApp them a secure link. When no `customer`
 * is supplied (KYC Forms tab) it first lets staff search for one.
 */
export const CollectKycDialog = ({ open, onClose, customer = null }) => {
  const navigate = useNavigate();
  const { branch: authBranch, branches } = useAuth();
  const [picked, setPicked] = useState(customer);
  const [branch, setBranch] = useState(authBranch || "DANDENONG");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open) {
      setPicked(customer || null);
      setBranch(authBranch || "DANDENONG");
      setQ("");
      setResults([]);
    }
  }, [open, customer, authBranch]);

  if (!open) return null;

  const search = async () => {
    if (q.trim().length < 2) return toast.error("Type a name or phone number");
    setSearching(true);
    try {
      const { data } = await api.get("/customers/lookup", { params: { q: q.trim() } });
      setResults(data || []);
      if (!data?.length) toast.message("No customers found on that");
    } catch {
      toast.error("Could not search customers");
    } finally {
      setSearching(false);
    }
  };

  const choose = (r) =>
    setPicked({
      name: r.customer_name || "",
      phone: r.customer_phone || "",
      email: r.customer_email || "",
      address: r.customer_address || "",
    });

  const fillAtCounter = () => {
    const sp = new URLSearchParams({
      name: picked.name || "",
      phone: picked.phone || "",
      email: picked.email || "",
      address: picked.address || "",
      branch,
    });
    onClose();
    navigate(`/kyc/collect?${sp.toString()}`);
  };

  const sendLink = async () => {
    if (!picked.phone) return toast.error("This customer has no mobile number on file");
    const res = await openIdRequest({ phone: picked.phone, customer_name: picked.name, branch });
    if (res) onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="collect-kyc-dialog"
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto border border-border bg-white p-6">
        <div className="flex items-start justify-between">
          <h2 className="text-base font-bold md:text-lg">Collect KYC</h2>
          <button onClick={onClose} data-testid="collect-kyc-close" aria-label="Close">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {!picked ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">Find the customer by name or phone.</p>
            <div className="flex gap-2">
              <Input
                className="h-10 rounded-none"
                placeholder="Name or mobile"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                data-testid="collect-kyc-search-input"
              />
              <Button
                variant="outline"
                className="h-10 whitespace-nowrap rounded-none"
                onClick={search}
                disabled={searching}
                data-testid="collect-kyc-search-button"
              >
                <Search className="mr-1.5 h-4 w-4" /> Find
              </Button>
            </div>
            {results.length > 0 && (
              <div className="divide-y divide-border border border-border" data-testid="collect-kyc-results">
                {results.slice(0, 20).map((r) => (
                  <button
                    key={r.customer_phone}
                    type="button"
                    onClick={() => choose(r)}
                    className="block w-full px-3 py-2.5 text-left text-sm hover:bg-[#FBF8F2]"
                    data-testid={`collect-kyc-result-${r.customer_phone}`}
                  >
                    <span className="font-semibold">{r.customer_name || "—"}</span>
                    <span className="ml-2 text-muted-foreground">
                      {formatPhone(r.customer_phone)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="border border-border bg-[#FBF8F2] p-3 text-sm" data-testid="collect-kyc-picked">
              <div className="font-semibold">{picked.name || "—"}</div>
              <div className="text-muted-foreground">
                {formatPhone(picked.phone) || "no mobile on file"}
              </div>
              {!customer && (
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="mt-1 text-xs text-[#8A6A22] underline"
                  data-testid="collect-kyc-change"
                >
                  Choose a different customer
                </button>
              )}
            </div>

            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Store
              </Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger className="mt-1.5 h-10 rounded-none" data-testid="collect-kyc-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-white">
                  {(branches || ["DANDENONG", "TRUGANINA"]).map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Button
                className="h-auto justify-start rounded-none bg-[#166534] py-4 text-left font-semibold text-white hover:bg-[#14532D]"
                onClick={fillAtCounter}
                data-testid="collect-kyc-counter"
              >
                <ClipboardCheck className="mr-2 h-5 w-5 shrink-0" />
                <span>
                  Fill in at the counter now
                  <span className="block text-xs font-normal text-white/80">
                    Photograph their two IDs (signature optional) — verified on the spot
                  </span>
                </span>
              </Button>
              <Button
                variant="outline"
                className="h-auto justify-start rounded-none py-4 text-left"
                onClick={sendLink}
                disabled={!picked.phone}
                data-testid="collect-kyc-sendlink"
              >
                <MessageCircle className="mr-2 h-5 w-5 shrink-0" />
                <span>
                  Send the customer a secure link
                  <span className="block text-xs font-normal text-muted-foreground">
                    WhatsApp them a link to fill it on their own phone
                  </span>
                </span>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
